import type { ApolloError } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { type FC, useRef, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import {
  ActionButtonIntent,
  AlertDialogComponent,
  ColorButton,
  HStack,
  Popover,
  TextField,
  TriggerButton,
  VisuallyHidden,
  VStack,
} from "widgets";
import formStyle from "widgets/form.module.css";
import { useOverlay } from "widgets/hooks";
import { object, string, type z } from "zod";

import { useFormAria } from "@/app-utils/form/useFormAria.ts";
import { PubSubActions } from "@/global/pubsub-actions.ts";
import type { InputItemTagInput } from "@/graphql.ts";
import { type GetTagsQuery, useAddTagMutation, useDeleteTagMutation } from "@/modules/projects/graphql/tags.gql.ts";
import type { GetArrayElementType } from "@/types";

export interface TagListItemProps {
  /**
   * Optional. If no tag is given the item is empty and in 'creation' mode
   */
  tag?: Omit<GetArrayElementType<GetTagsQuery["itemTags"]>, "project">;
  selectedProjectId: number;
  /**
   * Custom callback to refetch the listing of tags.
   * In addition, the form also notifies about changes to
   * subscribers of {@link PubSubActions.UPDATE_TAGS}
   */
  fetchCallback: () => Promise<unknown>;
}

const TagManagementSchema = object({
  name: string({ required_error: "Name is required" })
    .trim()
    .min(1, "Name is required")
    .max(100, "Name limited to 100 characters"),
  color: string().trim().nullable().optional(),
});

/**
 * Component to create or update ItemTags.
 *
 * When a tag is passed the component renders in 'editing' mode displaying a
 * delete button and passing the tag id to the endpoint for editing.
 *
 * When no tag is provided the component is in 'creation' mode and displays a
 * '+' button to create a new tag.
 *
 * When the color picker is closed the new color is automatically updated.
 *
 * To update the tags name just enter the new name and press ENTER.
 */
export const TagListItem: FC<TagListItemProps> = (props) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef(null);

  const { state, triggerProps, overlayProps } = useOverlay({});

  const [createOrUpdateTag] = useAddTagMutation();
  const [deleteTag, { loading }] = useDeleteTagMutation();

  const [isLoading, setIsLoading] = useState(false);

  const {
    handleSubmit,
    register,
    reset,
    setError,
    setFocus,
    setValue,
    watch,
    getValues,
    formState: { errors, isSubmitting, isDirty },
  } = useFormAria({
    defaultValues: { name: props.tag?.name ?? "", color: props.tag?.color ?? undefined },
    resolver: zodResolver(TagManagementSchema),
  });

  const onSubmit: SubmitHandler<z.infer<typeof TagManagementSchema>> = async (data) => {
    setIsLoading(true);
    await createOrUpdateTag({
      variables: {
        itemTagInputData: {
          id: props.tag?.id,
          colour: data.color,
          name: data.name,
          projectId: props.selectedProjectId,
        } satisfies InputItemTagInput,
      },
    })
      .then(() => {
        void props.fetchCallback();
        reset(props.tag ? { name: props.tag?.name, color: props.tag?.color ?? undefined } : undefined, {
          keepValues: !!props.tag,
        });

        PubSub.publish(PubSubActions.UPDATE_TAGS);
      })
      .catch((error) => {
        setError("name", { message: (error as ApolloError | Error).message });
        setFocus("name");
      });
    setIsLoading(false);
  };

  return (
    <form data-testid="edit_project_tag" onSubmit={handleSubmit(onSubmit)}>
      <VStack>
        <HStack>
          <ColorButton
            defaultColor={watch("color") ?? undefined}
            onApply={(color) => {
              setValue("color", color);
              if (props.tag) {
                void handleSubmit(onSubmit)();
              }
            }}
          />
          <TextField
            style={{ flex: 1 }}
            placeholder="New tag name"
            {...register("name")}
            defaultValue={props.tag?.name}
            isRequired
            isInvalid={isDirty && !!errors.name}
            maxLength={100}
            onBlur={() => {
              // isDirty: Prevent double submission on blurring out after already
              // submitted using the enter key
              // props.tag: only update exiting tag on blur, create tags only when pressing the button
              // or pressing the Enter key
              if (isDirty && props.tag) {
                void handleSubmit(onSubmit)();
              }
            }}
          />
          <VisuallyHidden>
            {/* Just useful when the user presses "Enter" */}
            <button type="submit" disabled={isSubmitting} tabIndex={-1} />
          </VisuallyHidden>

          {props.tag ? (
            <TriggerButton
              icon="delete"
              label=""
              key={`delete-tag-${props.tag?.id}`}
              aria-label="Remove tag"
              ref={triggerRef}
              {...triggerProps}
              isLoading={isLoading}
            />
          ) : (
            <TriggerButton
              icon="add"
              label="Add New Tag"
              key="add-new-tag"
              onPress={() => handleSubmit(onSubmit)()}
              isLoading={isLoading}
            />
          )}
          <Popover state={state} {...overlayProps} triggerRef={triggerRef} ref={popoverRef}>
            <AlertDialogComponent
              title="Remove Project Tag"
              mainButton={{
                icon: "delete",
                isLoading: loading,
                label: "Remove",
                intent: ActionButtonIntent.Danger,
                onPress: () =>
                  deleteTag({ variables: { itemTagId: props.tag?.id! } })
                    .then(() => {
                      void props.fetchCallback();
                      PubSub.publish(PubSubActions.UPDATE_TAGS);
                      state.close();
                    })
                    .catch((error) => {
                      setError("root", { message: (error as ApolloError | Error).message });
                    }),
              }}
              errorCallout={errors.root?.message ? { message: errors.root.message } : undefined}
              closeFn={() => {
                state.close();
              }}
            >
              Are you sure you want to remove &apos;{getValues("name")}&apos;?
            </AlertDialogComponent>
          </Popover>
        </HStack>
        {isDirty && !!errors.name && (
          <div role="alert" className={formStyle.error_message} data-testid="number-field-error">
            {errors.name?.message}
          </div>
        )}
      </VStack>
    </form>
  );
};
